import React, { useState, useContext } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  Snackbar,
  Alert,
  Modal,
  Backdrop,
  Fade,
  IconButton
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import { AppContext } from "../../../context/AppContext";

const BrandManager = () => {
  const { brands, refreshBrands } = useContext(AppContext);

  const [newBrand, setNewBrand] = useState("");
  const [editingBrand, setEditingBrand] = useState(null);
  const [editedBrandName, setEditedBrandName] = useState("");
  const [editedCode, setEditedCode] = useState("");
  const [openModal, setOpenModal] = useState(false);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleAddBrand = async () => {
    if (!newBrand.trim()) return;

    try {
      const response = await fetch("/categorias/marcas/agregar/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newBrand.trim() })
      });

      const result = await response.json();
      if (!response.ok) {
        setError(result.error || "No se pudo agregar la marca.");
        return;
      }

      setNewBrand("");
      setSuccessMessage("Marca añadida correctamente.");
      await refreshBrands();
    } catch (_) {
      setError("No se pudo agregar la marca.");
    }
  };

  const handleDeleteBrand = async () => {
    try {
      const response = await fetch(`/categorias/marcas/${editingBrand.id}/eliminar/`, {
        method: "DELETE"
      });

      if (!response.ok) throw new Error();

      setSuccessMessage("Marca eliminada correctamente.");
      setOpenModal(false);
      await refreshBrands();
    } catch (_) {
      setError("No se puede eliminar esta marca porque tiene productos asociados.");
    }
  };

  const handleEditBrand = (brand) => {
    setEditingBrand(brand);
    setEditedBrandName(brand.name);
    setEditedCode(brand.sequential_code || "");
    setOpenModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editedBrandName.trim() || !editedCode.trim()) return;

    try {
      const response = await fetch(`/categorias/marcas/${editingBrand.id}/editar/`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: editedBrandName.trim(),
          sequential_code: editedCode.trim()
        })
      });

      const result = await response.json();

      if (!response.ok) {
        setError(result.error || "No se pudo editar la marca.");
        return;
      }

      setSuccessMessage("Marca editada correctamente.");
      setOpenModal(false);
      await refreshBrands();
    } catch (_) {
      setError("No se pudo editar la marca.");
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: "#1565C0" }}>
        Gestión de Marcas
      </Typography>

      <Box display="flex" alignItems="center" gap={1} sx={{ mb: 3 }}>
        <TextField
          label="Nueva Marca"
          variant="outlined"
          size="small"
          value={newBrand}
          onChange={(e) => setNewBrand(e.target.value)}
          fullWidth
          InputProps={{
            endAdornment: newBrand && (
              <InputAdornment position="end">
                <IconButton onClick={() => setNewBrand("")} edge="end">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddBrand}
          startIcon={<AddIcon />}
          disabled={!newBrand.trim()}
          sx={{ whiteSpace: "nowrap" }}
        >
          Añadir
        </Button>
      </Box>

      {brands.length > 0 ? (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "center",
            flexGrow: 1,
            overflowY: "auto",
            p: 1
          }}
        >
          {brands.map((brand) => (
            <Button
              key={brand.id}
              variant="outlined"
              sx={{ textTransform: "none", minWidth: "140px" }}
              onClick={() => handleEditBrand(brand)}
            >
              {brand.name}
            </Button>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: "#666", textAlign: "center" }}>
          No hay marcas disponibles.
        </Typography>
      )}

      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={openModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "white",
              boxShadow: 24,
              p: 3,
              borderRadius: 2,
              width: 320
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Editar Marca
            </Typography>

            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Secuencial"
              value={editedCode}
              onChange={(e) => setEditedCode(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Nombre"
              value={editedBrandName}
              onChange={(e) => setEditedBrandName(e.target.value)}
              sx={{ mb: 3 }}
            />

            <Box display="flex" justifyContent="space-between">
              <Button color="error" onClick={handleDeleteBrand}>
                Eliminar
              </Button>
              <Button color="primary" onClick={handleSaveEdit}>
                Guardar
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      <Snackbar
        open={!!error}
        autoHideDuration={3000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error">{error}</Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success">{successMessage}</Alert>
      </Snackbar>
    </Box>
  );
};

export default BrandManager;
